import React, { useState } from 'react';
import { CreditCard, Download, Users, Zap, Database, ShieldCheck, Sparkles, CheckCircle2, AlertCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

export default function BillingSettings({ setMessage, currentCompany }) {
    const { user, userRole } = useAuth();
    const { currentTenant } = useTenant();
    const [selectedBillingCycle, setSelectedBillingCycle] = useState('annual');

    const emailLower = (user?.email || '').toLowerCase();
    const isCreatorAccount = 
        userRole === 'superadmin' || 
        ['sanjay', 'isanjaydev', 'admin@oblique.com', 'business.eleventh'].some(e => emailLower.includes(e));

    const plans = [
        {
            id: 'starter',
            name: 'Growth Starter',
            price: '$49',
            period: '/mo',
            desc: 'For small inbound sales teams getting started.',
            features: ['Up to 5 Users', 'Standard CRM Objects', '5,000 Dials/Month', 'Community Support']
        },
        {
            id: 'pro',
            name: 'Commercial Pro',
            price: '$199',
            period: '/mo',
            desc: 'For scaling teams that need deep call audits and QMS.',
            features: ['Up to 25 Users', 'Full QMS & Rubric AI', '50,000 Dials/Month', 'HubSpot & 3CX Sync', 'Priority Support']
        },
        {
            id: 'enterprise',
            name: 'Enterprise Scale + AI Suite',
            price: '$499',
            period: '/mo',
            desc: 'For high-volume contact centers and enterprise operations.',
            features: [
                'Unlimited Workspace Seats',
                'Dual-Write Data Warehouse (BigQuery)',
                '100,000+ Dials/Day Capacity',
                'Custom AI Rubrics & Audio QA',
                'Field-Level Security & Role Matrices',
                'Dedicated Technical Architect'
            ]
        }
    ];

    return (
        <div className="max-w-3xl animate-fade-in font-sans" style={{ margin: '0 auto', padding: '16px 0 64px' }}>
            {/* Header */}
            <div style={{ marginBottom: '32px' }}>
                <div className="flex items-center justify-between">
                    <div>
                        <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', letterSpacing: '-0.02em', marginBottom: '4px' }}>
                            Subscription & Billing
                        </h2>
                        <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                            Manage workspace subscription plans, usage thresholds, and licensing.
                        </p>
                    </div>

                    {isCreatorAccount && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-100 text-purple-800 border border-purple-200 text-xs font-bold shadow-xs">
                            <Sparkles size={14} className="text-purple-600" />
                            <span>Founder Tier (Comped Lifetime)</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Founder / Creator Lifetime Banner */}
            {isCreatorAccount ? (
                <div className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-white shadow-xl border border-slate-700 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
                        <Sparkles size={140} />
                    </div>

                    <div className="flex items-start justify-between relative z-10">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <span className="px-2.5 py-0.5 rounded-full bg-emerald-400/20 text-emerald-300 border border-emerald-400/30 text-[11px] font-bold tracking-wider uppercase">
                                    Active • Lifetime Comped
                                </span>
                                <span className="text-xs text-slate-400">Account ID: {currentTenant?.id || 'org-founder'}</span>
                            </div>
                            <h3 className="text-xl font-bold tracking-tight text-white mb-1">
                                Enterprise Scale + Oblique AI Suite
                            </h3>
                            <p className="text-xs text-slate-300 max-w-lg leading-relaxed">
                                As the creator and founding administrator of Oblique, your workspace is granted full unrestricted access to all top-tier enterprise modules, AI intelligence, and unlimited data warehouse storage free of charge for life.
                            </p>
                        </div>

                        <div className="text-right shrink-0">
                            <div className="text-3xl font-black text-white">$0.00</div>
                            <div className="text-xs text-emerald-400 font-semibold mt-0.5">Free for Life ($499/mo value)</div>
                        </div>
                    </div>

                    <div className="mt-6 pt-5 border-t border-slate-700/60 grid grid-cols-3 gap-4 text-center">
                        <div className="p-2.5 rounded-xl bg-white/5 border border-white/10">
                            <div className="text-xs text-slate-400 font-medium">Workspace Seats</div>
                            <div className="text-base font-bold text-white mt-0.5">Unlimited (∞)</div>
                        </div>
                        <div className="p-2.5 rounded-xl bg-white/5 border border-white/10">
                            <div className="text-xs text-slate-400 font-medium">AI QA Operations</div>
                            <div className="text-base font-bold text-white mt-0.5">Unlimited (∞)</div>
                        </div>
                        <div className="p-2.5 rounded-xl bg-white/5 border border-white/10">
                            <div className="text-xs text-slate-400 font-medium">Data Warehouse Capacity</div>
                            <div className="text-base font-bold text-white mt-0.5">100k+ Dials/Day</div>
                        </div>
                    </div>
                </div>
            ) : (
                /* Standard Tenant Plan Summary */
                <div className="mb-8 p-6 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border)] shadow-xs">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-800">
                                    30-Day Enterprise Trial
                                </span>
                                <span className="text-xs text-[var(--text-tertiary)]">24 Days Remaining</span>
                            </div>
                            <h3 className="text-lg font-bold text-[var(--text-primary)]">Enterprise Scale</h3>
                            <p className="text-xs text-[var(--text-secondary)] mt-0.5">Trial ends on Nov 1, 2026. No credit card required during trial.</p>
                        </div>
                        <button className="px-4 py-2 rounded-lg bg-[var(--text-primary)] text-[var(--bg-page)] text-xs font-bold cursor-pointer hover:opacity-90 transition-opacity">
                            Add Payment Method
                        </button>
                    </div>
                </div>
            )}

            {/* How Enterprise SaaS Billing Progression Works */}
            <div className="mb-8 p-6 rounded-xl bg-[var(--bg-surface)] border border-[var(--border)]">
                <h3 className="text-sm font-bold text-[var(--text-primary)] flex items-center gap-2 mb-3">
                    <ShieldCheck size={18} className="text-blue-500" />
                    How Oblique Handles Multi-Tenant Billing (HubSpot & Salesforce Model)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-[var(--text-secondary)]">
                    <div className="p-3.5 rounded-lg bg-[var(--bg-muted)] border border-[var(--border)]">
                        <div className="font-bold text-[var(--text-primary)] mb-1 flex items-center gap-1.5">
                            <span className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-[10px]">1</span>
                            Founder & Staff Bypass
                        </div>
                        <p className="leading-relaxed">
                            Creators, developers, and internal staff accounts are flagged with <code>isComped: true</code>. All subscription checkouts and billing locks are automatically bypassed.
                        </p>
                    </div>

                    <div className="p-3.5 rounded-lg bg-[var(--bg-muted)] border border-[var(--border)]">
                        <div className="font-bold text-[var(--text-primary)] mb-1 flex items-center gap-1.5">
                            <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px]">2</span>
                            30-Day Free Trial
                        </div>
                        <p className="leading-relaxed">
                            When a new tenant provisions a workspace, they receive an automatic 30-day enterprise trial without needing upfront payment details, exactly like HubSpot.
                        </p>
                    </div>

                    <div className="p-3.5 rounded-lg bg-[var(--bg-muted)] border border-[var(--border)]">
                        <div className="font-bold text-[var(--text-primary)] mb-1 flex items-center gap-1.5">
                            <span className="w-5 h-5 rounded-full bg-purple-500 text-white flex items-center justify-center text-[10px]">3</span>
                            Automated Stripe Portal
                        </div>
                        <p className="leading-relaxed">
                            Upon trial completion, customers transition into Stripe Customer Portal for self-serve credit card billing, automated invoicing, and receipt downloads.
                        </p>
                    </div>
                </div>
            </div>

            {/* Available Plans Comparison */}
            <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider">
                        Oblique Subscription Tiers
                    </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {plans.map(p => {
                        const isCurrent = p.id === 'enterprise';
                        return (
                            <div 
                                key={p.id}
                                className={`p-5 rounded-xl border flex flex-col justify-between transition-all ${
                                    isCurrent 
                                        ? 'bg-[var(--bg-surface)] border-purple-500 shadow-md ring-1 ring-purple-500/20' 
                                        : 'bg-[var(--bg-surface)] border-[var(--border)]'
                                }`}
                            >
                                <div>
                                    <div className="flex justify-between items-start mb-2">
                                        <h4 className="text-sm font-bold text-[var(--text-primary)]">{p.name}</h4>
                                        {isCurrent && (
                                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-800">
                                                Active
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-[11px] text-[var(--text-secondary)] mb-4">{p.desc}</p>
                                    <div className="flex items-baseline gap-1 mb-4">
                                        <span className="text-2xl font-black text-[var(--text-primary)]">{p.price}</span>
                                        <span className="text-xs text-[var(--text-tertiary)]">{p.period}</span>
                                    </div>

                                    <div className="space-y-2 border-t border-[var(--border)] pt-4">
                                        {p.features.map((feat, idx) => (
                                            <div key={idx} className="flex items-start gap-2 text-[11px] text-[var(--text-secondary)]">
                                                <CheckCircle2 size={13} className="text-emerald-500 shrink-0 mt-0.5" />
                                                <span>{feat}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-6 pt-3">
                                    <button 
                                        disabled={isCurrent}
                                        className={`w-full py-1.5 rounded text-xs font-bold transition-all ${
                                            isCurrent 
                                                ? 'bg-[var(--bg-muted)] text-[var(--text-tertiary)] cursor-default' 
                                                : 'bg-[var(--text-primary)] text-[var(--bg-page)] hover:opacity-90 cursor-pointer'
                                        }`}
                                    >
                                        {isCurrent ? 'Current Tier' : 'Switch Plan'}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Invoicing Contact */}
            <div className="mt-8 text-center text-xs text-[var(--text-tertiary)]">
                Have questions regarding custom enterprise contracts or multi-entity billing?{' '}
                <a href="mailto:billing@oblique.com" className="text-blue-500 font-semibold hover:underline">
                    Contact Enterprise Licensing
                </a>
            </div>
        </div>
    );
}
